import { useEffect, useRef, useState } from "react";

import {
  isDesktopDiagnosticsTab,
  type DesktopDiagnosticsTarget,
} from "./DiagnosticsDesktopTabs.logic";
import type { DiagnosticsTab, EvidenceTab } from "./DiagnosticsSettings.types";
import { correctDiagnosticsTabForServerTarget } from "./useDiagnosticsPanelState.logic";

export function useDiagnosticsTabs(
  desktopTargets: ReadonlyArray<DesktopDiagnosticsTarget>,
  serverTargetVisible: boolean,
) {
  // Opens on the server target when it is offered — the one machine always
  // readable, so a fresh page shows evidence rather than "select a target".
  // Where it is not offered, opening there would land on a panel that 403s.
  const [activeTab, setActiveTab] = useState<DiagnosticsTab>("cloud");
  const [evidenceTab, setEvidenceTab] = useState<EvidenceTab>("status");
  /**
   * Events is a view, not a machine, so leaving it has to land back on the
   * target the user was already inspecting rather than resetting to Cloud.
   */
  const fallbackTab: DiagnosticsTab = serverTargetVisible ? "server" : "cloud";
  const lastTargetTab = useRef<DiagnosticsTab>(fallbackTab);
  if (activeTab !== "events") lastTargetTab.current = activeTab;

  useEffect(() => {
    const activeDesktopTargetExists = desktopTargets.some((target) => target.tabId === activeTab);
    if (isDesktopDiagnosticsTab(activeTab) && !activeDesktopTargetExists) {
      setActiveTab(fallbackTab);
    }
  }, [activeTab, desktopTargets, fallbackTab]);

  useEffect(() => {
    if (!isDesktopDiagnosticsTab(lastTargetTab.current)) return;
    if (desktopTargets.some((target) => target.tabId === lastTargetTab.current)) return;
    lastTargetTab.current = fallbackTab;
  }, [desktopTargets, fallbackTab]);

  // The server target can stop being offered mid-session — CMMD answers the
  // platform-membership question after first paint, and can revise it. Sitting
  // on a tab the picker no longer lists is the state to correct.
  useEffect(() => {
    const corrected = correctDiagnosticsTabForServerTarget({ activeTab, serverTargetVisible });
    if (corrected !== null) setActiveTab(corrected);
  }, [activeTab, serverTargetVisible]);

  return {
    activeTab,
    evidenceTab,
    setActiveTab,
    setEvidenceTab,
    showRuntimeTab: () => setActiveTab(lastTargetTab.current),
  } as const;
}
